/**
 * Create Channel Modal Component
 * Modal for creating new channels
 */

import { useState } from 'react';
import { useCreateChannel } from '@/hooks/useChannels';
import { useAppStore } from '@/store/app.store';
import { useDialogA11y } from '@/hooks/useDialogA11y';

interface CreateChannelModalProps {
  onClose: () => void;
}

const CreateChannelModal = ({ onClose }: CreateChannelModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'public' | 'private' | 'announcement'>('public');
  const [error, setError] = useState<string | null>(null);

  const createChannelMutation = useCreateChannel();
  const { setActiveChannel } = useAppStore();
  const { dialogRef, titleId } = useDialogA11y(onClose);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Името на канала е задължително');
      return;
    }

    try {
      const newChannel = await createChannelMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        type,
      });
      setActiveChannel(newChannel.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Грешка при създаване на канал');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50" onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="bg-white dark:bg-dark-bg-elevated rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border-primary">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            Създай нов канал
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary rounded transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Name Input */}
          <div>
            <label
              htmlFor="channel-name"
              className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
            >
              Име на канала *
            </label>
            <input
              id="channel-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="например: general, marketing, разработка"
              maxLength={50}
              autoFocus
            />
          </div>

          {/* Description Input */}
          <div>
            <label
              htmlFor="channel-description"
              className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
            >
              Описание
            </label>
            <textarea
              id="channel-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input resize-none"
              rows={3}
              placeholder="Кратко описание на канала..."
              maxLength={200}
            />
          </div>

          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Тип канал *
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="channel-type"
                  value="public"
                  checked={type === 'public'}
                  onChange={(e) => setType(e.target.value as 'public')}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Публичен</div>
                  <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                    Всеки може да види и присъедини към този канал
                  </div>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="channel-type"
                  value="private"
                  checked={type === 'private'}
                  onChange={(e) => setType(e.target.value as 'private')}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Частен</div>
                  <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                    Само поканени потребители могат да видят този канал
                  </div>
                </div>
              </label>

              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="channel-type"
                  value="announcement"
                  checked={type === 'announcement'}
                  onChange={(e) => setType(e.target.value as 'announcement')}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Обяви</div>
                  <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                    Само администратори могат да публикуват съобщения
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={createChannelMutation.isPending}
            >
              Отказ
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={createChannelMutation.isPending}
            >
              {createChannelMutation.isPending ? 'Създаване...' : 'Създай канал'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateChannelModal;
